import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { 
  UserCheck, Calendar as CalendarIcon, Clock, CheckCircle, Download, 
  Loader2, Filter, Search, Plus, Trash2, Save, X, FileBarChart, Users, 
  ChevronLeft, ChevronRight, AlertCircle, Eye 
} from 'lucide-react';

const API = window.location.origin + '/api';
const TOKEN = () => localStorage.getItem('auth_token') || '';

async function api(path, opts = {}) {
  const h = { 'Authorization': 'Bearer ' + TOKEN(), 'Content-Type': 'application/json', ...opts.headers };
  const r = await fetch(API + path, { ...opts, headers: h });
  if (!r.ok) { const e = await r.json(); throw new Error(e.error || 'Request failed'); }
  return r.json();
}

function formatDate(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('id-ID', { year: 'numeric', month: 'short', day: 'numeric' });
}

function formatDateInput(iso) {
  if (!iso) return '';
  return new Date(iso).toISOString().split('T')[0];
}

function formatDateTime(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('id-ID', { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function hitungTotalJam(jamMulai, jamSelesai) {
  if (!jamMulai || !jamSelesai) return 0;
  const [h1, m1] = jamMulai.split(':').map(Number);
  const [h2, m2] = jamSelesai.split(':').map(Number);
  if (isNaN(h1) || isNaN(m1) || isNaN(h2) || isNaN(m2)) return 0;
  const mulai = h1 * 60 + m1;
  const selesai = h2 * 60 + m2;
  let selisih = selesai - mulai;
  if (selisih < 0) selisih += 24 * 60;
  return Math.round((selisih / 60) * 100) / 100;
}

// Badge tipe personil: nakes (petugas kesehatan) vs karyawan (non-nakes)
const TipeBadge = ({ tipe }) =>
  tipe === 'nakes' ? (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700">👨‍⚕️ Nakes</span>
  ) : (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700">🧑‍💼 Karyawan</span>
  );

const RekapAbsensi = () => {
  const [data, setData] = useState([]);
  const [rekapBulanan, setRekapBulanan] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [viewMode, setViewMode] = useState('list'); // 'list' | 'rekap'
  const [filterNama, setFilterNama] = useState('');
  const [filterTanggal, setFilterTanggal] = useState('');
  const [filterBulan, setFilterBulan] = useState(String(new Date().getMonth() + 1));
  const [filterTahun, setFilterTahun] = useState(String(new Date().getFullYear()));
  const [filterDari, setFilterDari] = useState('');
  const [filterSampai, setFilterSampai] = useState('');
  const [filterTipe, setFilterTipe] = useState(''); // '' | 'nakes' | 'karyawan'

  // Form state
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ nama: '', tanggal: '', jamMulai: '', jamSelesai: '', keterangan: '', tipe: 'karyawan' });
  const [saving, setSaving] = useState(false);

  // Detail view state
  const [detailItem, setDetailItem] = useState(null);
  const [detailRekap, setDetailRekap] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      let params = [];
      if (filterNama) params.push('nama=' + encodeURIComponent(filterNama));
      if (filterTanggal) params.push('tanggal=' + encodeURIComponent(filterTanggal));
      const qs = params.length ? '?' + params.join('&') : '';
      const d = await api('/absensi' + qs);
      setData(d.absensi || []);
      setError('');
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [filterNama, filterTanggal]);

  const fetchRekap = useCallback(async () => {
    // Rentang tanggal belum lengkap (hanya satu sisi terisi) → jangan fetch, tunggu user melengkapi
    if ((filterDari && !filterSampai) || (!filterDari && filterSampai)) {
      setRekapBulanan([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const params = [];
      if (filterDari && filterSampai) {
        params.push('dari=' + encodeURIComponent(filterDari));
        params.push('sampai=' + encodeURIComponent(filterSampai));
      } else {
        params.push('bulan=' + filterBulan);
        params.push('tahun=' + filterTahun);
      }
      const d = await api('/absensi/rekap-bulanan?' + params.join('&'));
      setRekapBulanan(d.rekap || []);
      setError('');
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [filterBulan, filterTahun, filterDari, filterSampai]);

  useEffect(() => {
    if (viewMode === 'list') fetchData();
    else fetchRekap();
  }, [viewMode, fetchData, fetchRekap]);

  const resetForm = () => {
    setForm({ nama: '', tanggal: '', jamMulai: '', jamSelesai: '', keterangan: '', tipe: 'karyawan' });
    setEditing(null);
    setShowForm(false);
  };

  const handleEdit = (item) => {
    setForm({
      nama: item.nama || '',
      tanggal: formatDateInput(item.tanggal),
      jamMulai: item.jamMulai || '',
      jamSelesai: item.jamSelesai || '',
      keterangan: item.keterangan || '',
      tipe: item.tipe || 'karyawan',
    });
    setEditing(item);
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.nama || !form.tanggal || !form.jamMulai || !form.jamSelesai) {
      setError('Nama, tanggal, jam mulai, dan jam selesai wajib diisi');
      return;
    }
    setSaving(true);
    try {
      const body = {
        nama: form.nama,
        tanggal: form.tanggal,
        jamMulai: form.jamMulai,
        jamSelesai: form.jamSelesai,
        keterangan: form.keterangan,
        tipe: form.tipe,
      };
      if (editing) {
        await api('/absensi/' + editing._id, { method: 'PUT', body: JSON.stringify(body) });
      } else {
        await api('/absensi', { method: 'POST', body: JSON.stringify(body) });
      }
      resetForm();
      fetchData();
      setError('');
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Hapus data absensi ini?')) return;
    try {
      await api('/absensi/' + id, { method: 'DELETE' });
      fetchData();
    } catch (e) {
      setError(e.message);
    }
  };

  // data tampil mengikuti filter tipe (Semua / Nakes / Karyawan)
  const filteredData = useMemo(
    () => data.filter((d) => !filterTipe || d.tipe === filterTipe),
    [data, filterTipe]
  );
  const filteredRekap = useMemo(
    () => rekapBulanan.filter((r) => !filterTipe || r.tipe === filterTipe),
    [rekapBulanan, filterTipe]
  );

  const totalJamRekap = useMemo(() => {
    return filteredRekap.reduce((sum, r) => sum + (r.totalJam || 0), 0);
  }, [filteredRekap]);

  const totalHariRekap = useMemo(() => {
    return filteredRekap.reduce((sum, r) => sum + (r.totalHari || 0), 0);
  }, [filteredRekap]);

  const bulanOptions = [
    { value: '1', label: 'Januari' }, { value: '2', label: 'Februari' },
    { value: '3', label: 'Maret' }, { value: '4', label: 'April' },
    { value: '5', label: 'Mei' }, { value: '6', label: 'Juni' },
    { value: '7', label: 'Juli' }, { value: '8', label: 'Agustus' },
    { value: '9', label: 'September' }, { value: '10', label: 'Oktober' },
    { value: '11', label: 'November' }, { value: '12', label: 'Desember' },
  ];
  const tahunOptions = [];
  for (let y = new Date().getFullYear(); y >= 2020; y--) {
    tahunOptions.push(String(y));
  }

  // Label periode yang sedang aktif (rentang tanggal atau bulan)
  const periodeLabel = useMemo(() => {
    if (filterDari && filterSampai) {
      return formatDate(filterDari) + ' — ' + formatDate(filterSampai);
    }
    return (bulanOptions.find(b => b.value === filterBulan)?.label || '') + ' ' + filterTahun;
  }, [filterDari, filterSampai, filterBulan, filterTahun]);

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Rekap Absensi</h2>
          <p className="mt-1 text-sm text-gray-500">Data kehadiran dan jam kerja personil</p>
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={() => { setViewMode('list'); resetForm(); }}
            className={`btn-secondary text-sm ${viewMode === 'list' ? 'border-blue-300 text-blue-700 bg-blue-50' : ''}`}
          >
            <Clock className="w-4 h-4" />
            <span>Data Harian</span>
          </button>
          <button
            onClick={() => { setViewMode('rekap'); resetForm(); }}
            className={`btn-secondary text-sm ${viewMode === 'rekap' ? 'border-blue-300 text-blue-700 bg-blue-50' : ''}`}
          >
            <FileBarChart className="w-4 h-4" />
            <span>Rekap Bulanan</span>
          </button>
          {viewMode === 'list' && (
            <button onClick={() => { resetForm(); setShowForm(true); }} className="btn-primary text-sm">
              <Plus className="w-4 h-4" />
              <span>Tambah</span>
            </button>
          )}
        </div>
      </div>

      {/* Filter tipe personil: nakes vs karyawan */}
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm font-medium text-gray-600">Tipe:</span>
        {[
          { value: '', label: 'Semua' },
          { value: 'nakes', label: '👨‍⚕️ Nakes' },
          { value: 'karyawan', label: '🧑‍💼 Karyawan' },
        ].map((t) => (
          <button
            key={t.value}
            onClick={() => setFilterTipe(t.value)}
            className={`btn-secondary text-sm ${filterTipe === t.value ? 'border-blue-300 text-blue-700 bg-blue-50' : ''}`}
          >
            {t.label}
          </button>
        ))}
        <span className="text-xs text-gray-400">{filterTipe ? (viewMode === 'list' ? filteredData.length : filteredRekap.length) + ' data' : ''}</span>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-3 flex items-center space-x-2">
          <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
          <p className="text-sm text-red-700 flex-1">{error}</p>
          <button onClick={() => setError('')} className="text-red-500 hover:text-red-700"><X className="w-4 h-4" /></button>
        </div>
      )}

      {/* ====== FORM TAMBAH/EDIT ====== */}
      {showForm && (
        <div className="card p-5 animate-slide-up border-2 border-blue-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">{editing ? 'Edit Absensi' : 'Tambah Absensi'}</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">Nama</label>
              <input type="text" value={form.nama} onChange={(e) => setForm({ ...form, nama: e.target.value })}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500" placeholder="Nama personil" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">Tipe</label>
              <select value={form.tipe} onChange={(e) => setForm({ ...form, tipe: e.target.value })}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500">
                <option value="karyawan">🧑‍💼 Karyawan (non-nakes)</option>
                <option value="nakes">👨‍⚕️ Nakes</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">Tanggal</label>
              <input type="date" value={form.tanggal} onChange={(e) => setForm({ ...form, tanggal: e.target.value })}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">Jam Mulai</label>
              <input type="time" value={form.jamMulai} onChange={(e) => setForm({ ...form, jamMulai: e.target.value })}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">Jam Selesai</label>
              <input type="time" value={form.jamSelesai} onChange={(e) => setForm({ ...form, jamSelesai: e.target.value })}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">Total Jam <span className="text-gray-400 font-normal">(otomatis)</span></label>
              <div className="w-full px-3 py-2 border border-gray-100 rounded-lg text-sm bg-gray-50 text-gray-700 font-semibold">
                {hitungTotalJam(form.jamMulai, form.jamSelesai)} jam
              </div>
            </div>
            <div className="md:col-span-2 lg:col-span-1">
              <label className="block text-sm font-medium text-gray-600 mb-1">Keterangan</label>
              <input type="text" value={form.keterangan} onChange={(e) => setForm({ ...form, keterangan: e.target.value })}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500" placeholder="Opsional" />
            </div>
          </div>
          <div className="flex items-center space-x-3 mt-4 pt-4 border-t border-gray-100">
            <button onClick={handleSave} disabled={saving} className="btn-primary text-sm">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              <span>{saving ? 'Menyimpan...' : 'Simpan'}</span>
            </button>
            <button onClick={resetForm} className="btn-secondary text-sm">
              <X className="w-4 h-4" />
              <span>Batal</span>
            </button>
          </div>
        </div>
      )}

      {/* ====== VIEW: DATA HARIAN ====== */}
      {viewMode === 'list' && (
        <>
          {/* Filters */}
          <div className="card p-4">
            <div className="flex flex-wrap items-center gap-3">
              <Filter className="w-4 h-4 text-gray-400" />
              <span className="text-sm font-medium text-gray-600">Filter:</span>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input type="text" placeholder="Cari nama..." value={filterNama}
                  onChange={(e) => setFilterNama(e.target.value)}
                  className="pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 w-48" />
              </div>
              <input type="date" value={filterTanggal} onChange={(e) => setFilterTanggal(e.target.value)}
                className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500" />
              <span className="text-xs text-gray-400">{filteredData.length} data</span>
            </div>
          </div>

          {/* Table */}
          <div className="card overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="text-left px-4 py-3 font-semibold text-gray-600">No</th>
                    <th className="text-left px-4 py-3 font-semibold text-gray-600">Nama</th>
                    <th className="text-left px-4 py-3 font-semibold text-gray-600">Tipe</th>
                    <th className="text-left px-4 py-3 font-semibold text-gray-600">Tanggal</th>
                    <th className="text-left px-4 py-3 font-semibold text-gray-600">Jam Mulai</th>
                    <th className="text-left px-4 py-3 font-semibold text-gray-600">Jam Selesai</th>
                    <th className="text-center px-4 py-3 font-semibold text-gray-600">Total Jam</th>
                    <th className="text-left px-4 py-3 font-semibold text-gray-600">Keterangan</th>
                    <th className="text-center px-4 py-3 font-semibold text-gray-600">Aksi</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan={9} className="text-center py-12"><Loader2 className="w-6 h-6 animate-spin mx-auto text-blue-500" /></td></tr>
                  ) : data.length === 0 ? (
                    <tr><td colSpan={9} className="text-center py-12 text-gray-400">
                      <FileBarChart className="w-12 h-12 mx-auto text-gray-200 mb-2" />
                      <p className="font-medium">Belum ada data absensi</p>
                      <p className="text-sm mt-1">Klik "Tambah" untuk menambahkan data</p>
                    </td></tr>
                  ) : (
                    filteredData.map((item, idx) => (
                      <tr key={item._id} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                        <td className="px-4 py-3 text-gray-500">{idx + 1}</td>
                        <td className="px-4 py-3 font-medium text-gray-900">{item.nama}</td>
                        <td className="px-4 py-3"><TipeBadge tipe={item.tipe || 'karyawan'} /></td>
                        <td className="px-4 py-3 text-gray-600">{formatDate(item.tanggal)}</td>
                        <td className="px-4 py-3 font-mono text-gray-700">{item.jamMulai}</td>
                        <td className="px-4 py-3 font-mono text-gray-700">{item.jamSelesai}</td>
                        <td className="px-4 py-3 text-center font-semibold">
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700">
                            {item.totalJam} jam
                          </span>
                        </td>
                        <td className="px-4 py-3 text-gray-500 max-w-[200px] truncate">{item.keterangan || '—'}</td>
                        <td className="px-4 py-3 text-center">
                          <div className="flex items-center justify-center space-x-1">
                            <button onClick={() => setDetailItem(item)} className="p-1.5 text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors" title="Lihat Detail">
                              <Eye className="w-4 h-4" />
                            </button>
                            <button onClick={() => handleEdit(item)} className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors" title="Edit">
                              ✏️
                            </button>
                            <button onClick={() => handleDelete(item._id)} className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg transition-colors" title="Hapus">
                              🗑️
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* ====== VIEW: REKAP BULANAN ====== */}
      {viewMode === 'rekap' && (
        <>
          {/* Periode Picker — rentang tanggal atau bulan */}
          <div className="card p-4">
            <div className="flex flex-wrap items-center gap-3">
              <CalendarIcon className="w-4 h-4 text-gray-400" />
              <span className="text-sm font-medium text-gray-600">Dari:</span>
              <input type="date" value={filterDari} onChange={(e) => setFilterDari(e.target.value)}
                className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500" />
              <span className="text-sm font-medium text-gray-600">Sampai:</span>
              <input type="date" value={filterSampai} onChange={(e) => setFilterSampai(e.target.value)}
                className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500" />
              {(filterDari && !filterSampai) || (!filterDari && filterSampai) ? (
                <span className="text-xs text-amber-600 bg-amber-50 px-2.5 py-1 rounded-lg flex items-center gap-1">
                  <AlertCircle className="w-3.5 h-3.5" />
                  Lengkapi tanggal dari & sampai
                </span>
              ) : null}
              <div className="flex items-center gap-2 px-2 text-gray-300">
                <span className="w-8 border-t border-gray-200" />
              </div>
              <span className="text-sm font-medium text-gray-600">Atau Bulan:</span>
              <select value={filterBulan} onChange={(e) => { setFilterBulan(e.target.value); setFilterDari(''); setFilterSampai(''); }}
                className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500">
                {bulanOptions.map(b => <option key={b.value} value={b.value}>{b.label}</option>)}
              </select>
              <select value={filterTahun} onChange={(e) => { setFilterTahun(e.target.value); setFilterDari(''); setFilterSampai(''); }}
                className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500">
                {tahunOptions.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
              <button onClick={fetchRekap} className="btn-primary text-sm">
                <Filter className="w-4 h-4" />
                <span>Tampilkan</span>
              </button>
            </div>
          </div>

          {/* Summary Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="card p-5">
              <div className="flex items-center space-x-3">
                <div className="p-3 rounded-xl bg-blue-50"><Users className="w-5 h-5 text-blue-600" /></div>
                <div>
                  <p className="text-sm text-gray-500">Total Personil</p>
                  <p className="text-2xl font-bold text-gray-900">{filteredRekap.length}</p>
                </div>
              </div>
            </div>
            <div className="card p-5">
              <div className="flex items-center space-x-3">
                <div className="p-3 rounded-xl bg-green-50"><CalendarIcon className="w-5 h-5 text-green-600" /></div>
                <div>
                  <p className="text-sm text-gray-500">Total Hari Kerja</p>
                  <p className="text-2xl font-bold text-gray-900">{totalHariRekap}</p>
                </div>
              </div>
            </div>
            <div className="card p-5">
              <div className="flex items-center space-x-3">
                <div className="p-3 rounded-xl bg-purple-50"><Clock className="w-5 h-5 text-purple-600" /></div>
                <div>
                  <p className="text-sm text-gray-500">Total Jam Kerja</p>
                  <p className="text-2xl font-bold text-gray-900">{totalJamRekap.toFixed(1)} jam</p>
                </div>
              </div>
            </div>
          </div>

          {/* Rekap Table — Simplified */}
          <div className="card overflow-hidden">
            <div className="card-header">
              <h3 className="section-title">{filterDari && filterSampai ? 'Rekap Rentang Tanggal' : 'Rekap Bulanan'}</h3>
              <span className="text-sm text-gray-400">{periodeLabel}</span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-200">
                    <th className="text-left px-4 py-3 font-semibold text-gray-600">No</th>
                    <th className="text-left px-4 py-3 font-semibold text-gray-600">Nama</th>
                    <th className="text-center px-4 py-3 font-semibold text-gray-600">Total Hari Kerja</th>
                    <th className="text-center px-4 py-3 font-semibold text-gray-600">Total Jam</th>
                    <th className="text-center px-4 py-3 font-semibold text-gray-600">Aksi</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan={5} className="text-center py-12"><Loader2 className="w-6 h-6 animate-spin mx-auto text-blue-500" /></td></tr>
                  ) : filteredRekap.length === 0 ? (
                    <tr><td colSpan={5} className="text-center py-12 text-gray-400">
                      <FileBarChart className="w-12 h-12 mx-auto text-gray-200 mb-2" />
                      <p className="font-medium">Belum ada data untuk periode ini</p>
                    </td></tr>
                  ) : (
                    filteredRekap.map((r, idx) => (
                      <tr key={r.nama} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                        <td className="px-4 py-3 text-gray-500">{idx + 1}</td>
                        <td className="px-4 py-3">
                          <div className="flex items-center space-x-2">
                            <span className="font-medium text-gray-900">{r.nama}</span>
                            <TipeBadge tipe={r.tipe || 'karyawan'} />
                          </div>
                        </td>
                        <td className="px-4 py-3 text-center font-semibold text-gray-700">{r.totalHari} hari</td>
                        <td className="px-4 py-3 text-center font-semibold text-blue-700">{r.totalJam.toFixed(1)} jam</td>
                        <td className="px-4 py-3 text-center">
                          <button
                            onClick={() => setDetailRekap(r)}
                            className="inline-flex items-center space-x-1 px-3 py-1.5 text-xs font-medium text-emerald-700 bg-emerald-50 hover:bg-emerald-100 rounded-lg transition-colors"
                          >
                            <Eye className="w-3.5 h-3.5" />
                            <span>Lihat Detail</span>
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
                <tfoot>
                  <tr className="bg-gray-50 font-semibold">
                    <td colSpan={2} className="px-4 py-3 text-gray-700">Total</td>
                    <td className="px-4 py-3 text-center text-gray-900">{totalHariRekap} hari</td>
                    <td className="px-4 py-3 text-center text-blue-800">{totalJamRekap.toFixed(1)} jam</td>
                    <td />/>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        </>
      )}

      {/* ====== MODAL DETAIL REKAP PER TANGGAL ====== */}
      {detailRekap && (
        <div className="fixed inset-0 z-50 overflow-y-auto animate-fade-in" onClick={() => setDetailRekap(null)}>
          <div className="flex items-center justify-center min-h-screen px-4 py-8">
            <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" />
            <div className="relative bg-white rounded-2xl shadow-2xl max-w-2xl w-full z-10 animate-scale-in" onClick={e => e.stopPropagation()}>
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
                <div className="flex items-center space-x-3">
                  <div className="p-2.5 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 text-white">
                    <UserCheck className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900">{detailRekap.nama}</h3>
                    <p className="text-sm text-gray-500">Detail kehadiran — {detailRekap.totalHari} hari, {detailRekap.totalJam.toFixed(1)} jam</p>
                  </div>
                </div>
                <button onClick={() => setDetailRekap(null)} className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="px-6 py-4 max-h-[60vh] overflow-y-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="text-left px-3 py-2 text-xs font-semibold text-gray-500">Tanggal</th>
                      <th className="text-left px-3 py-2 text-xs font-semibold text-gray-500">Jam Mulai</th>
                      <th className="text-left px-3 py-2 text-xs font-semibold text-gray-500">Jam Selesai</th>
                      <th className="text-center px-3 py-2 text-xs font-semibold text-gray-500">Total</th>
                      <th className="text-left px-3 py-2 text-xs font-semibold text-gray-500">Keterangan</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detailRekap.records.map((rec) => (
                      <tr key={rec._id} className="border-b border-gray-50 hover:bg-gray-50 transition-colors">
                        <td className="px-3 py-2.5 font-medium text-gray-900">{formatDate(rec.tanggal)}</td>
                        <td className="px-3 py-2.5 font-mono text-gray-700">{rec.jamMulai}</td>
                        <td className="px-3 py-2.5 font-mono text-gray-700">{rec.jamSelesai}</td>
                        <td className="px-3 py-2.5 text-center">
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700">{rec.totalJam} jam</span>
                        </td>
                        <td className="px-3 py-2.5 text-gray-500 max-w-[200px] truncate">{rec.keterangan || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="px-6 py-3 border-t border-gray-100 flex justify-between items-center bg-gray-50 rounded-b-2xl">
                <span className="text-sm text-gray-500">{detailRekap.records.length} record</span>
                <button onClick={() => setDetailRekap(null)} className="btn-primary text-sm">
                  <X className="w-4 h-4" />
                  <span>Tutup</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ====== MODAL DETAIL DATA ====== */}
      {detailItem && (
        <div className="fixed inset-0 z-50 overflow-y-auto animate-fade-in" onClick={() => setDetailItem(null)}>
          <div className="flex items-center justify-center min-h-screen px-4 py-8">
            <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" />
            <div className="relative bg-white rounded-2xl shadow-2xl max-w-lg w-full z-10 animate-scale-in" onClick={e => e.stopPropagation()}>
              {/* Header */}
              <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100">
                <div className="flex items-center space-x-3">
                  <div className="p-2.5 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 text-white">
                    <Eye className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900">Detail Absensi</h3>
                    <p className="text-sm text-gray-500">Informasi lengkap data inputan</p>
                  </div>
                </div>
                <button onClick={() => setDetailItem(null)} className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors" title="Tutup">
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Body */}
              <div className="px-6 py-5 space-y-4">
                {/* Identitas utama */}
                <div className="flex items-center space-x-4 pb-4 border-b border-gray-100">
                  <div className="flex-shrink-0 w-14 h-14 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
                    <UserCheck className="w-7 h-7 text-white" />
                  </div>
                  <div>
                    <h4 className="text-xl font-bold text-gray-900 flex items-center space-x-2">
                      <span>{detailItem.nama}</span>
                      <TipeBadge tipe={detailItem.tipe || 'karyawan'} />
                    </h4>
                    <p className="text-sm text-gray-500 mt-0.5">Data absensi harian</p>
                  </div>
                </div>

                {/* Grid info */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-gray-50 rounded-xl p-4">
                    <label className="block text-xs font-medium text-gray-500 mb-1">Tanggal</label>
                    <p className="text-sm font-semibold text-gray-900">{formatDate(detailItem.tanggal)}</p>
                  </div>
                  <div className="bg-gray-50 rounded-xl p-4">
                    <label className="block text-xs font-medium text-gray-500 mb-1">Total Jam</label>
                    <p className="text-sm font-bold text-blue-700">
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50">
                        {detailItem.totalJam || 0} jam
                      </span>
                    </p>
                  </div>
                  <div className="bg-gray-50 rounded-xl p-4">
                    <label className="block text-xs font-medium text-gray-500 mb-1">Jam Mulai</label>
                    <p className="text-sm font-bold font-mono text-gray-900">{detailItem.jamMulai}</p>
                  </div>
                  <div className="bg-gray-50 rounded-xl p-4">
                    <label className="block text-xs font-medium text-gray-500 mb-1">Jam Selesai</label>
                    <p className="text-sm font-bold font-mono text-gray-900">{detailItem.jamSelesai}</p>
                  </div>
                </div>

                {/* Keterangan lengkap */}
                <div className="bg-gray-50 rounded-xl p-4">
                  <label className="block text-xs font-medium text-gray-500 mb-1">Keterangan</label>
                  <p className="text-sm font-semibold text-gray-900 whitespace-pre-wrap break-words">{detailItem.keterangan || '—'}</p>
                </div>

                {/* Meta waktu */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="bg-gray-50 rounded-xl p-4">
                    <label className="block text-xs font-medium text-gray-500 mb-1">Dibuat pada</label>
                    <p className="text-sm font-semibold text-gray-900">{formatDateTime(detailItem.createdAt)}</p>
                  </div>
                  <div className="bg-gray-50 rounded-xl p-4">
                    <label className="block text-xs font-medium text-gray-500 mb-1">Diperbarui pada</label>
                    <p className="text-sm font-semibold text-gray-900">{formatDateTime(detailItem.updatedAt)}</p>
                  </div>
                </div>

                {/* Aksi footer */}
                <div className="flex justify-end space-x-3 pt-2">
                  <button onClick={() => { handleEdit(detailItem); setDetailItem(null); }} className="btn-secondary text-sm">
                    ✏️
                    <span>Edit</span>
                  </button>
                  <button onClick={() => setDetailItem(null)} className="btn-primary text-sm">
                    <X className="w-4 h-4" />
                    <span>Tutup</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RekapAbsensi;
